import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, ArrowUpRight } from "lucide-react";

const comparisonData = [
  {
    id: "fixa",
    title: "Renda fixa",
    rate: "1% a.m",
    total: "R$ 944.000",
    growth: "+844%",
    bars: [15, 25, 38, 55, 75],
    highlight: false,
  },
  {
    id: "alavancagem",
    title: "Alavancagem imobiliária",
    rate: "3% a.m",
    total: "R$ 5.205.000",
    growth: "+5105%",
    bars: [20, 40, 65, 85, 100],
    highlight: true,
  },
  {
    id: "acoes",
    title: "Ações",
    rate: "1.5% a.m",
    total: "R$ 3.120.000",
    growth: "+3020%",
    bars: [18, 32, 50, 70, 90],
    highlight: false,
  }
];

const labels = ["Aplic.", "5 anos", "10 anos", "15 anos", "20 anos"];

export default function Profitability() {
  return (
    <section id="rentabilidade" className="py-24 lg:py-32 relative overflow-hidden" data-testid="profitability-section">
      <div className="container relative z-10">
        <div className="max-w-3xl mb-16">
          <div className="eyebrow mb-4"><span className="kicker-line" />Performance</div>
          <h2 className="text-4xl md:text-5xl font-display font-light mb-6">
            A dinâmica do <span className="italic text-gold-gradient">mercado</span>.
          </h2>
          <p className="text-muted-foreground max-w-2xl leading-relaxed">
            Compare as projeções históricas de diferentes veículos de investimento. Meu papel é desenhar o cenário mais eficiente para o seu momento, sempre expondo de forma clara os prazos, os custos contratuais e as variáveis de mercado.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {comparisonData.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15, duration: 0.8 }}
              className={`relative p-8 rounded-sm border transition-all duration-500 ${
                item.highlight 
                  ? "bg-foreground text-background border-transparent shadow-2xl scale-105 z-20" 
                  : "bg-card-gradient border-primary/20 text-foreground z-10"
              }`}
            >
              {item.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">
                  Estratégia Recomendada
                </div>
              )}

              <div className="mb-12">
                <div className={`text-xs uppercase tracking-widest mb-1 ${item.highlight ? "text-background/60" : "text-muted-foreground"}`}>
                  {item.title} — {item.rate}
                </div>
                <div className="flex items-end gap-3">
                  <span className="text-3xl font-display leading-none">{item.total}</span>
                  <span className={`text-sm flex items-center gap-1 mb-1 ${item.highlight ? "text-green-600 font-bold" : "text-primary font-medium"}`}>
                    <ArrowUpRight size={16} />
                    {item.growth}
                  </span>
                </div>
              </div>

              {/* Chart Area */}
              <div className="relative h-64 flex items-end justify-between gap-1 lg:gap-2 mb-4 px-2">
                {item.bars.map((height, i) => (
                  <div key={i} className="flex-1 flex flex-col items-end group h-full justify-end">
                    <motion.div 
                      initial={{ height: "0%" }}
                      whileInView={{ height: `${height}%` }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ delay: (idx * 0.1) + (i * 0.05), duration: 1, ease: "easeOut" }}
                      className={`w-full min-w-[12px] md:min-w-[20px] max-w-[40px] rounded-t-[2px] relative transition-colors duration-300 ${
                        item.highlight 
                          ? "bg-green-500/80 group-hover:bg-green-500" 
                          : "bg-primary/60 group-hover:bg-primary"
                      }`}
                    >
                      {/* Tooltip-like value for the last bar */}
                      {i === item.bars.length - 1 && (
                        <div className={`absolute -top-10 left-1/2 -translate-x-1/2 text-[10px] font-mono whitespace-nowrap px-2 py-1 rounded border shadow-lg z-30 ${
                          item.highlight ? "bg-background text-foreground border-border" : "bg-primary text-primary-foreground border-transparent"
                        }`}>
                          {item.total}
                        </div>
                      )}
                    </motion.div>
                  </div>
                ))}
              </div>

              {/* X-Axis Labels */}
              <div className="flex justify-between items-center px-1">
                {labels.map((label, i) => (
                  <span key={i} className={`text-[10px] uppercase tracking-tighter ${item.highlight ? "text-background/40" : "text-muted-foreground/50"}`}>
                    {label}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-center text-xs text-muted-foreground italic max-w-3xl mx-auto opacity-60">
          *Os valores acima representam simulações baseadas na rentabilidade média histórica e não configuram promessa, garantia ou resultado típico. A adequação da estratégia depende do seu fluxo de caixa, horizonte e tolerância a riscos.
        </p>
      </div>

      {/* Decorative Background Element */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none -z-10 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 blur-[120px] rounded-full" />
      </div>
    </section>
  );
}
